import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  DollarSign,
  Gauge,
  Loader2,
  RefreshCw,
  Users,
  Building2,
} from "lucide-react";
import { requirementsAPI } from "../../../services/requirementsAPI";

const money = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: Number(value || 0) < 0.01 ? 4 : 2,
    maximumFractionDigits: Number(value || 0) < 0.01 ? 6 : 2,
  }).format(Number(value || 0));

const number = (value) => new Intl.NumberFormat("en-US").format(Number(value || 0));

const errorMessage = (error) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    try {
      return JSON.stringify(detail);
    } catch {
      return "Could not load AI usage.";
    }
  }
  return error?.message || "Could not load AI usage.";
};

const Metric = ({ icon: Icon, label, value, helper }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="flex items-center justify-between text-slate-500">
      <span className="text-xs font-bold uppercase tracking-wide">{label}</span>
      <Icon className="h-4 w-4" />
    </div>
    <div className="mt-2 text-2xl font-black text-slate-900">{value}</div>
    <div className="mt-1 text-xs text-slate-500">{helper}</div>
  </div>
);

const BudgetBar = ({ label, spend, limit, format = number }) => {
  const enabled = Number(limit || 0) > 0;
  const ratio = enabled ? Math.min(1, Number(spend || 0) / Number(limit)) : 0;
  return (
    <div>
      <div className="flex items-center justify-between gap-3 text-xs font-bold text-slate-600">
        <span>{label}</span>
        <span className="text-right">
          {enabled ? `${format(spend)} / ${format(limit)}` : "No limit configured"}
        </span>
      </div>
      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-200">
        <div
          className={`h-full rounded-full ${
            ratio >= 0.9 ? "bg-rose-500" : ratio >= 0.75 ? "bg-amber-500" : "bg-emerald-500"
          }`}
          style={{ width: `${Math.max(enabled ? 2 : 0, ratio * 100)}%` }}
        />
      </div>
    </div>
  );
};

const ScopeTable = ({ title, rows, emptyText }) => (
  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div className="border-b border-slate-200 px-4 py-3">
      <h3 className="font-black text-slate-900">{title}</h3>
    </div>
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-3">Name</th>
            <th className="px-4 py-3">Calls</th>
            <th className="px-4 py-3">Input</th>
            <th className="px-4 py-3">Output</th>
            <th className="px-4 py-3">Cost</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {(rows || []).map((row) => (
            <tr key={row.id}>
              <td className="max-w-xs truncate px-4 py-3 font-semibold text-slate-800" title={row.name}>
                {row.name}
              </td>
              <td className="px-4 py-3">{number(row.calls)}</td>
              <td className="px-4 py-3">{number(row.input_tokens)}</td>
              <td className="px-4 py-3">{number(row.output_tokens)}</td>
              <td className="px-4 py-3 font-black text-slate-900">{money(row.estimated_cost)}</td>
            </tr>
          ))}
          {(rows || []).length === 0 && (
            <tr>
              <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                {emptyText}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const GlobalAIUsageView = () => {
  const [catalog, setCatalog] = useState(null);
  const [usage, setUsage] = useState(null);
  const [projectId, setProjectId] = useState("");
  const [userId, setUserId] = useState("");
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { days: Number(days) || 30 };
      if (projectId) params.project_id = Number(projectId);
      if (userId) params.user_id = Number(userId);
      const response = await requirementsAPI.adminUsage(params);
      setUsage(response.data || null);
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [days, projectId, userId]);

  useEffect(() => {
    let active = true;
    requirementsAPI
      .adminUsage({ days: 30 })
      .then((response) => {
        if (active) setCatalog(response.data || null);
      })
      .catch((catalogError) => {
        if (active) setError(errorMessage(catalogError));
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const projectOptions = useMemo(
    () => catalog?.filters?.projects || catalog?.by_project || [],
    [catalog],
  );
  const userOptions = useMemo(
    () => catalog?.filters?.users || catalog?.by_user || [],
    [catalog],
  );
  const limits = usage?.budgets?.limits || {};
  const projectBudget = usage?.budgets?.project || {};
  const userBudget = usage?.budgets?.user || {};

  return (
    <div className="space-y-5">
      <div className="flex flex-col justify-between gap-3 lg:flex-row lg:items-end">
        <div>
          <div className="flex items-center gap-2">
            <DollarSign className="h-6 w-6 text-orange-600" />
            <h1 className="text-2xl font-black text-slate-900">AI Cost & Limits</h1>
          </div>
          <p className="mt-1 text-sm text-slate-500">
            Global admin visibility across projects, users, models, operations, and configured budgets.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Refresh
        </button>
      </div>

      <div className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-3">
        <label className="text-xs font-bold uppercase tracking-wide text-slate-500">
          Project
          <select
            value={projectId}
            onChange={(event) => setProjectId(event.target.value)}
            className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-normal text-slate-800"
          >
            <option value="">All projects</option>
            {projectOptions.map((row) => (
              <option key={row.id} value={row.id}>{row.name}</option>
            ))}
          </select>
        </label>
        <label className="text-xs font-bold uppercase tracking-wide text-slate-500">
          User
          <select
            value={userId}
            onChange={(event) => setUserId(event.target.value)}
            className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-normal text-slate-800"
          >
            <option value="">All users</option>
            {userOptions.map((row) => (
              <option key={row.id} value={row.id}>{row.name}</option>
            ))}
          </select>
        </label>
        <label className="text-xs font-bold uppercase tracking-wide text-slate-500">
          Period
          <select
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
            className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-normal text-slate-800"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={366}>Last 366 days</option>
          </select>
        </label>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          {error}
        </div>
      )}

      {usage && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Metric icon={DollarSign} label="Today" value={money(usage.today?.estimated_cost)} helper={`${number(usage.today?.calls)} requests`} />
            <Metric icon={Gauge} label="Selected period" value={money(usage.selected_period?.estimated_cost)} helper={`${number(usage.selected_period?.input_tokens)} input tokens`} />
            <Metric icon={Users} label="Users" value={number(usage.by_user?.length)} helper="Users with recorded activity" />
            <Metric icon={Building2} label="Projects" value={number(usage.by_project?.length)} helper={`${number(usage.unpriced_calls)} unpriced calls`} />
          </div>

          <div className="grid gap-4 xl:grid-cols-3">
            <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-black text-slate-900">Configured project limits</h3>
              <BudgetBar label="Daily USD" spend={projectBudget.daily_spend} limit={projectBudget.daily_limit || limits.project_daily_usd} format={money} />
              <BudgetBar label="Monthly USD" spend={projectBudget.monthly_spend} limit={projectBudget.monthly_limit || limits.project_monthly_usd} format={money} />
              <BudgetBar label="Daily tokens" spend={projectBudget.daily_tokens} limit={projectBudget.daily_token_limit || limits.project_daily_tokens} />
              {!projectId && <p className="text-xs text-slate-500">Select a project to show actual spend against its limits.</p>}
            </div>
            <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-black text-slate-900">Configured user limits</h3>
              <BudgetBar label="Daily USD" spend={userBudget.daily_spend} limit={userBudget.daily_limit || limits.user_daily_usd} format={money} />
              <BudgetBar label="Monthly USD" spend={userBudget.monthly_spend} limit={userBudget.monthly_limit || limits.user_monthly_usd} format={money} />
              <BudgetBar label="Daily tokens" spend={userBudget.daily_tokens} limit={userBudget.daily_token_limit || limits.user_daily_tokens} />
              {!userId && <p className="text-xs text-slate-500">Select a user to show actual spend against their limits.</p>}
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-black text-slate-900">Model pricing status</h3>
              <div className="mt-3 space-y-2">
                {(usage.pricing?.models || []).map((row) => (
                  <div key={row.model || row.id} className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2 text-sm">
                    <span className="min-w-0 truncate font-semibold text-slate-700" title={row.model}>{row.model}</span>
                    <span className={`shrink-0 rounded-full px-2 py-1 text-xs font-bold ${row.configured ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
                      {row.configured ? "Configured" : "Missing"}
                    </span>
                  </div>
                ))}
                {(usage.pricing?.models || []).length === 0 && (
                  <div className="text-sm text-slate-500">No configured models were returned.</div>
                )}
              </div>
            </div>
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <ScopeTable title="Cost by project" rows={usage.by_project} emptyText="No project usage in this scope." />
            <ScopeTable title="Cost by user" rows={usage.by_user} emptyText="No user usage in this scope." />
            <ScopeTable title="Cost by model" rows={usage.by_model} emptyText="No model usage in this scope." />
            <ScopeTable title="Cost by operation" rows={usage.by_operation} emptyText="No operation usage in this scope." />
          </div>
        </>
      )}
    </div>
  );
};

export default GlobalAIUsageView;
